import {useState, useEffect} from 'react';
import {breakpointValues} from '../styles/theme/breakpoints';

type Breakpoint = keyof typeof breakpointValues;

export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0
  });

  const [scrollSize, setScrollSize] = useState({
    width: 0,
    height: 0
  });

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    const handleResize = () => {
      setWindowSize({
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight
      });

      setScrollSize({
        width: document.documentElement.scrollWidth,
        height: document.documentElement.scrollHeight
      });
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  const isBreakpoint = (breakpoint: Breakpoint): boolean => {
    if (!isMounted) return false;
    return windowSize.width >= breakpointValues[breakpoint];
  };

  const isMobile = !isMounted ? false : windowSize.width < breakpointValues.sm;
  const isTabletOrSmaller = !isMounted
    ? false
    : windowSize.width < breakpointValues.md;
  const isLaptopOrSmaller = !isMounted
    ? false
    : windowSize.width < breakpointValues.lg;
  const isDesktopOrLarger = !isMounted
    ? false
    : windowSize.width >= breakpointValues.lg;

  return {
    windowSize,
    scrollSize,
    isMounted,
    isMobile,
    isTabletOrSmaller,
    isLaptopOrSmaller,
    isDesktopOrLarger,
    isBreakpoint,
    isXs: !isMounted ? false : windowSize.width < breakpointValues.sm,
    isSm: !isMounted
      ? false
      : windowSize.width >= breakpointValues.sm &&
        windowSize.width < breakpointValues.md,
    isMd: !isMounted
      ? false
      : windowSize.width >= breakpointValues.md &&
        windowSize.width < breakpointValues.lg,
    isLg: !isMounted
      ? false
      : windowSize.width >= breakpointValues.lg &&
        windowSize.width < breakpointValues.xl,
    isXl: !isMounted ? false : windowSize.width >= breakpointValues.xl,
    breakpoint:
      windowSize.width >= breakpointValues.xl
        ? 'xl'
        : windowSize.width >= breakpointValues.lg
          ? 'lg'
          : windowSize.width >= breakpointValues.md
            ? 'md'
            : windowSize.width >= breakpointValues.sm
              ? 'sm'
              : 'xs'
  };
};
